import '../../i18n/autoTranslate';
import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import SpotlightCard from '../../../components/ui/SpotlightCard';

const NotesPanel = ({ 
  isVisible = false,
  onToggle,
  sessionId = null
}) => {
  const [notes, setNotes] = useState('');
  const [savedNotes, setSavedNotes] = useState([]);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const textareaRef = useRef(null);
  const autoSaveTimeoutRef = useRef(null);

  // Load saved notes on mount (simulate localStorage with state)
  useEffect(() => {
    // In a real implementation, this would use localStorage
    // For demo purposes, we'll use component state
    const mockSavedData = {
      currentNotes: '',
      savedNotes: [],
      lastSaved: new Date().toISOString()
    };
    
    setNotes(mockSavedData.currentNotes || '');
    setSavedNotes(mockSavedData.savedNotes || []);
  }, [sessionId]);

  // Auto-save functionality
  useEffect(() => {
    if (autoSaveTimeoutRef?.current) {
      clearTimeout(autoSaveTimeoutRef?.current);
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      if (notes?.trim()) {
        saveNotes();
      }
    }, 2000);

    return () => {
      if (autoSaveTimeoutRef?.current) {
        clearTimeout(autoSaveTimeoutRef?.current);
      }
    };
  }, [notes]);

  const saveNotes = () => {
    setIsAutoSaving(true);
    
    // Simulate save operation
    setTimeout(() => setIsAutoSaving(false), 1000);
  };

  const addQuickNote = (noteText) => {
    const newNote = {
      id: Date.now(),
      text: noteText,
      timestamp: new Date().toISOString(),
      questionNumber: Math.floor(Math.random() * 10) + 1
    };

    const updatedSavedNotes = [...savedNotes, newNote];
    setSavedNotes(updatedSavedNotes);
  };

  const deleteNote = (noteId) => {
    const updatedNotes = savedNotes?.filter(note => note?.id !== noteId);
    setSavedNotes(updatedNotes);
  };

  const clearAllNotes = () => {
    setNotes('');
    setSavedNotes([]);
  };

  const quickNoteTemplates = [
    "Great question - need to elaborate",
    "Remember to mention specific example",
    "Follow up on this topic",
    "Good point to expand on",
    "Research this company aspect"
  ];

  if (!isVisible) return null;

  return (
    <div style={{zIndex:99999999999,height:'550px'}} className="fixed right-4 top-24 bottom-24  w-80  animate-slide-in overflow-y-scroll">
      <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.15)">
        <div className="bg-gradient-to-br from-[rgba(30,30,46,0.95)] via-[rgba(30,30,46,0.9)] to-[rgba(46,46,69,0.6)] border border-white/10 rounded-lg overflow-hidden h-full backdrop-blur">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            <div className="flex items-center space-x-2">
              <Icon name="FileText" size={20} className="text-indigo-400" />
              <h3 className="font-semibold text-white">Session Notes</h3>
            </div>
            
            <div className="flex items-center space-x-2">
              {isAutoSaving && (
                <div className="flex items-center space-x-1 text-xs text-green-400">
                  <Icon name="Check" size={12} />
                  <span>Saved</span>
                </div>
              )}
              
              <button
                onClick={onToggle}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors duration-200"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          </div>

          {/* Notes Content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Current Notes */}
            <div className="p-4 border-b border-gray-700">
              <label className="text-sm font-medium text-white mb-2 block">
                Current Notes
              </label>
              <textarea
                ref={textareaRef}
                value={notes}
                onChange={(e) => setNotes(e?.target?.value)}
                placeholder="Jot down your thoughts, key points, or questions during the interview..."
                className="w-full h-32 bg-gray-800/50 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500"
              />
            </div>

            {/* Quick Note Templates */}
            <div className="p-4 border-b border-gray-700">
              <label className="text-sm font-medium text-white mb-2 block">
                Quick Notes
              </label>
              <div className="space-y-2">
                {quickNoteTemplates?.map((template, index) => (
                  <button
                    key={index}
                    onClick={() => addQuickNote(template)}
                    className="w-full text-left text-xs text-gray-400 hover:text-white bg-gray-800/30 hover:bg-gray-700/50 rounded-lg px-3 py-2 transition-colors duration-150 border border-gray-700/50 hover:border-gray-600"
                  >
                    + {template}
                  </button>
                ))}
              </div>
            </div>

            {/* Saved Notes */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-white">
                  Saved Notes ({savedNotes?.length})
                </label>
                {savedNotes?.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Trash2"
                    onClick={clearAllNotes}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    Clear All
                  </Button>
                )}
              </div>

              <div className="space-y-3">
                {savedNotes?.length === 0 ? (
                  <div className="text-center py-8">
                    <Icon name="FileText" size={32} className="text-gray-500 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">No saved notes yet</p>
                    <p className="text-xs text-gray-500">Use quick notes or type above</p>
                  </div>
                ) : (
                  savedNotes?.map((note) => (
                    <div key={note?.id} className="bg-gray-800/30 border border-gray-700/50 rounded-lg p-3 space-y-2">
                      <div className="flex items-start justify-between">
                        <p className="text-sm text-white flex-1">{note?.text}</p>
                        <button
                          onClick={() => deleteNote(note?.id)}
                          className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-colors duration-150 ml-2"
                        >
                          <Icon name="X" size={12} />
                        </button>
                      </div>
                      
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>Q{note?.questionNumber}</span>
                        <span>{new Date(note.timestamp)?.toLocaleTimeString()}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Footer Actions */}
            <div className="p-4 border-t border-gray-700">
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  className="flex-1 border-gray-600 hover:border-gray-500 text-gray-300 hover:text-white"
                  onClick={() => {
                    const allNotes = {
                      currentNotes: notes,
                      savedNotes: savedNotes,
                      exportDate: new Date().toISOString()
                    };
                    const blob = new Blob([JSON.stringify(allNotes, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `interview-notes-${sessionId}.json`;
                    a?.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  Export
                </Button>
                
                <Button
                  variant="default"
                  size="sm"
                  iconName="Save"
                  onClick={saveNotes}
                  className="flex-1 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
                >
                  Save Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </SpotlightCard>
    </div>
  );
};

export default NotesPanel;
